import React, { useMemo, useRef, useState } from "react";
import { FaTruck, FaArrowUpRightFromSquare, FaPersonWalking } from "react-icons/fa6";
import moment from "moment";
import {
  formatValue,
  OrderStatusInverseEnum,
  statusColor,
} from "../../utils/Utils.js";
import Tooltip from "../../components/Tooltip.jsx";
import Badge from "../../components/Badges.jsx";
import { useSmallSize } from "../../utils/UseSmallSize.jsx";
import { useTheme } from "@/hooks/useTheme.js";
import { useSelector } from "react-redux";
import CanView from "@/components/CanView.jsx";

function OrdersTableItem({
  order,
  handleOpenOrder,
  isPast,
  onOpenCustomerModal,
}) {

  const { isMP } = useTheme();
  const { user } = useSelector((state) => state.app);

  const [nextUpdate, setNextUpdate] = useState(moment());
  const updateInterval = useRef();
  const [nextScheduledDateUpdate, setNextScheduledDateUpdate] = useState(
    moment()
  );
  const updateScheduledDateInterval = useRef();
  const isSmallSize = useSmallSize();

  // Calculate refund status for past orders
  const refundStatus = useMemo(() => {
    if (!isPast || !order.payments) return null;

    // Calculate total captured amount
    const totalCaptured =
      order.payments
        ?.filter((payment) => payment.charge_status === 2)
        ?.reduce((sum, payment) => sum + (payment.hold_amount || 0), 0) || 0;
    // Calculate total refunded amount (only refunds with corresponding captures)
    const totalRefunded =
      order.payments
        ?.filter((payment) => {
          if (payment.charge_status !== -1) return false; // Must be a refund

          if (payment.nmi_transaction_id) {
            return true;
          }

          // Check if there's a corresponding successful payment with same payment_intent_id
          let compareField = 'payment_intent_id'
          if(payment.payment_intent_id) {
            compareField = 'payment_intent_id'
          } else if(payment.frame_charge_intent_id) {
            compareField = 'frame_charge_intent_id'
          } else if(payment.nmi_transaction_id) {
            compareField = 'nmi_transaction_id'
          }

          const hasSuccessfulPayment = order.payments?.some(
            (otherPayment) =>
              otherPayment[compareField] === payment[compareField] &&
              otherPayment.charge_status === 2
          );

          return hasSuccessfulPayment;
        })
        ?.reduce((sum, payment) => sum + (payment.hold_amount || 0), 0) || 0;

    if (totalRefunded === 0) return null; // No refunds

    const totalCapturedDollars = totalCaptured / 100;
    const totalRefundedDollars = totalRefunded / 100;

    if (totalRefundedDollars >= totalCapturedDollars) {
      return { type: "fully_refunded", amount: totalRefundedDollars };
    } else {
      return { type: "partially_refunded", amount: totalRefundedDollars };
    }
  }, [order?.payments, isPast]);
  if (!order) return null;

  const OrderType = useMemo(() => {
    switch (order.order_type) {
      case "pickup":
        return {
          icon: (
            <svg
              viewBox="-8 -8 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4.87617 3.75H19.1238L21 8.86683V10.5C21 11.2516 20.7177 11.9465 20.25 12.4667V21H3.75V12.4667C3.28234 11.9465 3 11.2516 3 10.5V8.86683L4.87617 3.75ZM18.1875 13.3929C18.3807 13.3929 18.5688 13.3731 18.75 13.3355V19.5H15V15H9L9 19.5H5.25V13.3355C5.43122 13.3731 5.61926 13.3929 5.8125 13.3929C6.63629 13.3929 7.36559 13.0334 7.875 12.4667C8.38441 13.0334 9.11371 13.3929 9.9375 13.3929C10.7613 13.3929 11.4906 13.0334 12 12.4667C12.5094 13.0334 13.2387 13.3929 14.0625 13.3929C14.8863 13.3929 15.6156 13.0334 16.125 12.4667C16.6344 13.0334 17.3637 13.3929 18.1875 13.3929ZM10.5 19.5H13.5V16.5H10.5L10.5 19.5ZM19.5 9.75V10.5C19.5 11.2965 18.8856 11.8929 18.1875 11.8929C17.4894 11.8929 16.875 11.2965 16.875 10.5V9.75H19.5ZM19.1762 8.25L18.0762 5.25H5.92383L4.82383 8.25H19.1762ZM4.5 9.75V10.5C4.5 11.2965 5.11439 11.8929 5.8125 11.8929C6.51061 11.8929 7.125 11.2965 7.125 10.5V9.75H4.5ZM8.625 9.75V10.5C8.625 11.2965 9.23939 11.8929 9.9375 11.8929C10.6356 11.8929 11.25 11.2965 11.25 10.5V9.75H8.625ZM12.75 9.75V10.5C12.75 11.2965 13.3644 11.8929 14.0625 11.8929C14.7606 11.8929 15.375 11.2965 15.375 10.5V9.75H12.75Z"
                fill="#080341"
              />
            </svg>
          ),
          type: "Pickup",
        };
      case "delivery":
        return {
          icon: <FaTruck />,
          type: "Delivery",
        };
      case "in-store":
        return {
          icon: <FaPersonWalking />,
          type: "In-store",
        };
    }
    return null;
  }, [order.order_type]);

  const updateDate = () => {
    setNextUpdate(moment());
  };
  const formatAbsoluteDate = (date) => {
    const dateMoment = moment(date);
    if (dateMoment.year() === moment().year()) {
      return dateMoment.format("MMM DD, LT");
    }
    return dateMoment.format("MMM DD, YYYY, LT");
  };
  const displayDate = useMemo(() => {
    if (updateInterval.current) {
      clearInterval(updateInterval.current);
    }

    if (moment().diff(moment(order.order_date), "hours") < 12) {
      updateInterval.current = setInterval(updateDate, 60000);
      return moment(order.order_date).fromNow();
    } else {
      return formatAbsoluteDate(order.order_date);
    }
  }, [order.order_date, nextUpdate]);

  const updateScheduledDate = () => {
    setNextScheduledDateUpdate(moment());
  };
  const displayScheduledDate = useMemo(() => {
    if (!order.scheduled) return null;
    if (updateScheduledDateInterval.current) {
      clearInterval(updateScheduledDateInterval.current);
    }

    if (moment().diff(moment(order.scheduled), "hours") < 12) {
      updateScheduledDateInterval.current = setInterval(
        updateScheduledDate,
        60000
      );
      return moment(order.scheduled).fromNow();
    } else {
      return formatAbsoluteDate(order.scheduled);
    }
  }, [order.scheduled, nextScheduledDateUpdate]);

  if (!isSmallSize) {
    return (
      <tbody className={`text-sm __tutorial-orders-table_${order._id}`}>
        {/* Row */}
        <tr>
          <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
            <div className="flex items-center text-slate-800">
              <CanView directOnly>
                <div className="text-2xl w-10 h-10 shrink-0 flex items-center justify-center bg-slate-100 dark:bg-slate-700 rounded-full mr-2 sm:mr-3">
                  {OrderType.icon}
                </div>
              </CanView>
              <div
                onClick={() => {
                  const routeId = order.direct ? order.order_number : order.easy_id;
                  handleOpenOrder(routeId);
                }}
                className="font-medium text-sky-500 cursor-pointer"
              >
                {isMP ? order.easy_id : order.order_number}
                {isMP && (
                  <div className="text-xs text-slate-500">
                    {order.order_number}
                  </div>
                )}
              </div>
            </div>
          </td>
          <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
            {order.order_completed && (
              <div>
                Completed: {formatAbsoluteDate(order.order_completed)}
              </div>
            )}
            <div>
              {isPast && "Placed: "} {displayDate}
            </div>
            {order.scheduled && <div>Scheduled: {displayScheduledDate}</div>}
          </td>
          <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
            <div className="font-medium text-slate-800 dark:text-slate-100 flex items-center gap-2">
              <span>
                {order.order_type === 'in-store' ? 'In-Store' : `${order.customer.first_name} ${order.customer.last_name}`}
              </span>
              {!isMP ||
                ((user.type === "saucey_admin" && order.order_type !== 'in-store') && (
                  <button
                    onClick={() => onOpenCustomerModal(order.customer.email)}
                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                    title="Open customer profile"
                  >
                    <FaArrowUpRightFromSquare className="w-3 h-3" />
                  </button>
                ))}
            </div>
          </td>
          <CanView directOnly>
          <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
            <div className="font-medium text-slate-800 dark:text-slate-100">
              {OrderType.type}
              </div>
            </td>
          </CanView>
          <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
            <div className="text-left font-medium text-emerald-500">
              {order.products.reduce((acc, item) => {
                return acc + item.quantity;
              }, 0)}
            </div>
          </td>
          <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
            <div className="flex gap-1 items-start flex-col">
              <div className="flex gap-1 items-center">
                <div
                  className={`inline-flex font-medium rounded-full text-center px-2.5 py-0.5 ${statusColor(
                    refundStatus?.type === "fully_refunded"
                      ? "REFUNDED"
                      : OrderStatusInverseEnum[order.order_status],
                    order.queue?.state
                  )}`}
                >
                  {refundStatus?.type === "fully_refunded"
                    ? "REFUNDED"
                    : (
                        OrderStatusInverseEnum[
                          order.queue?.state === 4 ? 4 : order.order_status
                        ] || ""
                      )
                        .replace("_", " ")
                        .replace("IN PROGRESS", "EN ROUTE")}
                </div>
                {order.failed_reason && (
                  <Tooltip>{order.failed_reason} </Tooltip>
                )}
              </div>
              {order.stripe_3ds_url && (
                <Badge size="sm" color="red">
                  PENDING PAYMENT AUTH
                </Badge>
              )}
              {refundStatus && (
                <div className="mt-1">
                  <Badge
                    size="sm"
                    color={
                      refundStatus.type === "fully_refunded" ? "red" : "orange"
                    }
                  >
                    {refundStatus.type === "fully_refunded"
                      ? "FULLY REFUNDED"
                      : "PARTIALLY REFUNDED"}
                  </Badge>
                </div>
              )}
            </div>
          </td>
          <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
            {formatValue(order.direct ? order.total : order.dashboard_total)}
          </td>
          <td
            className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap"
            onClick={() => {
              handleOpenOrder(order._id);
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="icon icon-tabler icon-tabler-eye cursor-pointer stroke-current"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
              <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6" />
            </svg>
          </td>
        </tr>
      </tbody>
    );
  } else {
    return (
      <div
        className={`px-2 text-xs py-1.5 __tutorial-orders-table_${order._id}`}
        onClick={() => {
          const routeId = order.direct ? order.order_number : order.easy_id;
          handleOpenOrder(routeId);
        }}
      >
        {/*space between*/}
        <div className={"flex text-sm items-center justify-between"}>
          <div className={"font-bold"}>
            <CanView directOnly>
            {OrderType.type} - {order.order_number}
            </CanView>
            <CanView mpOnly>
              {order.easy_id} - {order.order_number}
            </CanView>
          </div>
          <div>{formatValue(order.total)}</div>
        </div>
        <div className="flex items-center gap-2">
          <span>
            {order.customer.first_name} {order.customer.last_name}
          </span>
          {!isMP ||
            (user.type === "saucey_admin" && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenCustomerModal(order.customer.email);
                }}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                title="Open customer profile"
              >
                <FaArrowUpRightFromSquare className="w-3 h-3" />
              </button>
            ))}
          <span>•</span>
          <span>
            {order.products.reduce((acc, item) => {
              return acc + item.quantity;
            }, 0)}{" "}
            items • {displayDate}
          </span>
        </div>
        {order.scheduled && !order.order_completed && (
          <div>
            <div>Scheduled: {displayScheduledDate}</div>
          </div>
        )}
        {order.order_completed && (
          <div>
            <div>Completed: {moment(order.order_completed).format("lll")}</div>
          </div>
        )}
        <div>
          <div className="flex items-center gap-2">
          <Badge
            customColor={statusColor(
              refundStatus?.type === "fully_refunded"
                ? "REFUNDED"
                : OrderStatusInverseEnum[order.order_status],
              order.queue?.state
            )}
            slim
            size="xs"
          >
            {refundStatus?.type === "fully_refunded"
              ? "REFUNDED"
              : (
                  OrderStatusInverseEnum[
                    order.queue?.state === 4 ? 4 : order.order_status
                  ] || ""
                )
                  .replace("_", " ")
                  .replace("IN PROGRESS", "EN ROUTE")}
          </Badge>
          {order.failed_reason && <Tooltip>{order.failed_reason} </Tooltip>}
          </div>
          
          {order.stripe_3ds_url && (
            <Badge
              size="2xs"
              customColor={statusColor("ACCEPTED", 1)}
              color="red"
              slim
            >
              PENDING PAYMENT AUTH
            </Badge>
          )}
          {refundStatus && (
            <div className="mt-1">
              <Badge
                size="2xs"
                color={
                  refundStatus.type === "fully_refunded" ? "red" : "orange"
                }
                slim
              >
                {refundStatus.type === "fully_refunded"
                  ? "FULLY REFUNDED"
                  : "PARTIALLY REFUNDED"}
              </Badge>
            </div>
          )}
          
        </div>
      </div>
    );
  }
}

export default OrdersTableItem;
